import React, { useState, useEffect } from 'react';

const HolographicSecurityLoader = ({ onComplete, duration = 1000 }) => {
  const [stage, setStage] = useState('loading');
  const [statusText, setStatusText] = useState('Initializing Holographic Security...');

  useEffect(() => {
    const statusSequence = [
      { time: duration * 0.17, text: 'Scanning Authentication...' },         // ~17% through  
      { time: duration * 0.58, text: 'Verifying Credentials...' },           // ~58% through
      { time: duration, text: 'ACCESS GRANTED' }                             // 100%
    ];

    const timers = statusSequence.map(({ time, text }) =>
      setTimeout(() => setStatusText(text), time)
    );

    // Success state - green glow for 1 second
    const successTimer = setTimeout(() => {
      setStage('success');
    }, duration);

    // Complete and fade out after 1 second of green glow
    const completeTimer = setTimeout(() => {
      setStage('complete');
      if (onComplete) onComplete();
    }, duration + 1000);

    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(successTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete, duration]);

  const styles = {
    loadingOverlay: {
      background: 'linear-gradient(135deg, rgba(13, 17, 23, 0.95), rgba(10, 15, 25, 0.98))',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '300px', // Fixed height instead of 100vh
      width: '100%',
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 9999,
      borderRadius: '16px',
      maxWidth: '500px',
      boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      opacity: stage === 'complete' ? 0 : 1,
      transition: 'opacity 0.5s ease-out',
    },
    hologramContainer: {
      position: 'relative',
      width: '120px',
      height: '120px',
      margin: '20px 0',
    },
    hologramBase: {
      position: 'absolute',
      bottom: '0',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '12px',
      background: stage === 'success' 
        ? 'linear-gradient(90deg, #0d4f3c, #39d87a, #0d4f3c)'
        : 'linear-gradient(90deg, #30363d, #58a6ff, #30363d)',
      borderRadius: '6px',
      boxShadow: stage === 'success'
        ? '0 0 20px rgba(57, 216, 122, 0.6)'
        : '0 0 15px rgba(88, 166, 255, 0.4)',
      transition: 'all 0.3s ease-in-out',
    },
    hologramProjection: {
      position: 'absolute',
      width: '90px',
      height: '90px',
      border: stage === 'success' ? '3px solid #39d87a' : '3px solid #58a6ff',
      borderRadius: '50%',
      top: '15px',
      left: '50%',
      transform: 'translateX(-50%)',
      animation: stage !== 'complete' ? 'hologramFlicker 2s ease-in-out infinite' : 'none',
      boxShadow: stage === 'success'
        ? '0 0 30px rgba(57, 216, 122, 0.8), inset 0 0 20px rgba(57, 216, 122, 0.3)'
        : '0 0 20px rgba(88, 166, 255, 0.6), inset 0 0 15px rgba(88, 166, 255, 0.2)',
      transition: 'all 0.3s ease-in-out',
    },
    hologramIcon: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      fontSize: '32px',
      animation: stage !== 'complete' ? 'hologramRotate 4s linear infinite' : 'none',
      filter: stage === 'success'
        ? 'drop-shadow(0 0 10px #39d87a)'
        : 'drop-shadow(0 0 8px #58a6ff)',
      transition: 'filter 0.3s ease-in-out',
    },
    projectionLines: {
      position: 'absolute',
      bottom: '12px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '3px',
      height: '90px',
      background: stage === 'success'
        ? 'linear-gradient(to top, #39d87a, transparent)'
        : 'linear-gradient(to top, #58a6ff, transparent)',
      animation: stage !== 'complete' ? 'beamIntensity 2s ease-in-out infinite' : 'none',
      transition: 'background 0.3s ease-in-out',
    },
    projectionLineLeft: {
      position: 'absolute',
      bottom: '0',
      left: '-20px',
      width: '3px',
      height: '90px',
      background: stage === 'success'
        ? 'linear-gradient(to top, #39d87a, transparent)'
        : 'linear-gradient(to top, #58a6ff, transparent)',
      transform: 'rotate(-20deg)',
      transformOrigin: 'bottom center',
      opacity: 0.7,
      transition: 'background 0.3s ease-in-out',
    },
    projectionLineRight: {
      position: 'absolute',
      bottom: '0',
      right: '-20px',
      width: '3px',
      height: '90px',
      background: stage === 'success'
        ? 'linear-gradient(to top, #39d87a, transparent)'
        : 'linear-gradient(to top, #58a6ff, transparent)',
      transform: 'rotate(20deg)',
      transformOrigin: 'bottom center',
      opacity: 0.7,
      transition: 'background 0.3s ease-in-out',
    },
    statusText: {
      marginTop: '30px',
      fontSize: '16px',
      color: stage === 'success' ? '#39d87a' : '#e6edf3',
      fontWeight: '500',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      textShadow: stage === 'success' ? '0 0 10px #39d87a' : 'none',
      transition: 'all 0.3s ease-in-out',
    },
    securityBadge: {
      position: 'absolute',
      top: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: stage === 'success' 
        ? 'rgba(57, 216, 122, 0.15)'
        : 'rgba(88, 166, 255, 0.1)',
      color: stage === 'success' ? '#39d87a' : '#58a6ff',
      padding: '6px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      border: stage === 'success'
        ? '1px solid rgba(57, 216, 122, 0.4)'
        : '1px solid rgba(88, 166, 255, 0.3)',
      boxShadow: stage === 'success'
        ? '0 0 15px rgba(57, 216, 122, 0.3)'
        : '0 0 10px rgba(88, 166, 255, 0.2)',
      transition: 'all 0.3s ease-in-out',
    },
  };

  const keyframes = `
    @keyframes hologramFlicker {
      0%, 100% { 
        opacity: 0.8; 
        transform: translateX(-50%) scale(1);
      }
      50% { 
        opacity: 1; 
        transform: translateX(-50%) scale(1.05);
      }
    }
    
    @keyframes hologramRotate {
      0% { transform: translate(-50%, -50%) rotateY(0deg); }
      100% { transform: translate(-50%, -50%) rotateY(360deg); }
    }
    
    @keyframes beamIntensity {
      0%, 100% { opacity: 0.6; }
      50% { opacity: 1; }
    }
  `;

  return (
    <>
      <style>{keyframes}</style>
      <div style={styles.loadingOverlay}>
        
        <div style={styles.hologramContainer}>
          <div style={styles.hologramBase}></div>
          
          <div style={styles.projectionLines}>
            <div style={styles.projectionLineLeft}></div>
            <div style={styles.projectionLineRight}></div>
          </div>
          
          <div style={styles.hologramProjection}>
            <div style={styles.hologramIcon}>
              {stage === 'success' ? '✓' : '🔒'}
            </div>
          </div>
        </div>
        
        <div style={styles.statusText}>
          {statusText}
        </div>
      </div>
    </>
  );
};

export default HolographicSecurityLoader;